import { captureException } from '@sentry/react';
import { all, takeEvery, put, call } from 'typed-redux-saga/macro';
import callApi, { METHODS, PATHS } from 'api';
import { ERROR_MESSAGES, STORAGE_KEYS, isDevWithAuthBypass, devRefreshToken, devUserId } from 'utils/constants';
import {
  resetState as resetLoginState,
  setError as setLoginError,
  setLoading as setLoginLoading,
} from 'features/Login/slice';
import { storageGetItem, storageRemoveItem, storageSetItem } from 'utils/localStorage';
import { authClient } from 'utils/capawesomeAuth';

import { fetchUserInfo, fetchUserUsage, setAuthData } from './actions';
import {
  SIGN_IN,
  SET_AUTH_DATA,
  INITIAL_AUTH,
  FETCH_USER_INFO,
  FETCH_USER_USAGE,
  LOGOUT,
} from './actionTypes';
import {
  IAuthResponse,
  ISetAuthData,
  TSignIn,
  IUserInfoResponse,
  IUserUsageResponse,
  TTier,
} from './types';
import {
  resetState,
  setAccessToken,
  setInitialAuthDone,
  setNotAuthorizedTier,
  setRefreshToken,
  setUserId,
  setUserTier,
  setUserTotalConversationCount,
  setFirebaseAuthenticated,
} from './slice';
import { setConversationSpeed } from 'features/ConversationControl/slice';
import { setEmail, setFirstName, setPreferredLanguageCode, setOnboardedAt, setStudyInNativeLanguage } from 'features/User/slice';

interface ISignInSaga {
  type: typeof SIGN_IN;
  payload: TSignIn;
}

interface ISetAuthDataSaga {
  type: typeof SIGN_IN;
  payload: ISetAuthData;
}


function* fetchUserInfoSaga() {
  try {
    const {
      data: { email, id, tier, preferences, first_name, onboarded_at },
    } = yield* call(callApi<object, IUserInfoResponse>, {
      method: METHODS.GET,
      mainPath: PATHS.USER_INFO,
      authorized: true,
    });

    yield* put(setEmail(email));
    yield* put(setFirstName(first_name || ''));
    yield* put(setOnboardedAt(onboarded_at));

    yield* put(setUserId(id));
    yield* put(setUserTier(tier));
    yield* put(
      setNotAuthorizedTier(!(['subscriber', 'free_trial_user', 'free'] as TTier[]).includes(tier))
    );

    if (preferences?.bot_speaking_speed) {
      yield* put(setConversationSpeed(preferences?.bot_speaking_speed));
    }
    
    if (preferences?.preferred_language_code !== undefined) {
      yield* put(setPreferredLanguageCode(preferences.preferred_language_code));
    }
    
    if (preferences?.study_in_native_language !== undefined) {
      yield* put(setStudyInNativeLanguage(preferences.study_in_native_language));
    }
  } catch (error) {
    yield* put(setNotAuthorizedTier(true));

    captureException(error);
  }
}

function* fetchUserUsageSaga() {
  try {
    const userUsageResponse = yield* call(callApi<object, IUserUsageResponse>, {
      method: METHODS.GET,
      mainPath: PATHS.USER_USAGE,
      authorized: true,
      queryParams: [
        { key: 'start_date', value: '2023-01-01' },
        { key: 'end_date', value: '2023-01-01' },
      ],
    });

    yield* put(setUserTotalConversationCount(userUsageResponse.data.total_conversation_count));
  } catch (error) {
    captureException(error);
  }
}

// TODO(Donatas): Remove this saga as it is now handled by firebase auth
export function* signInSaga(action: ISignInSaga) {
  try {
    const response = yield* call(callApi<{ email: string; password: string }, IAuthResponse>, {
      method: METHODS.POST,
      mainPath: PATHS.LOGIN,
      data: {
        email: action.payload.email,
        password: action.payload.password,
      },
    });

    yield* put(
      setAuthData({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
      })
    );

    yield* put(resetLoginState());
  } catch {
    if (action.payload.flow === 'login') {
      yield* put(setLoginError(ERROR_MESSAGES.WRONG_EMAIL_PASS));
    }

    yield* put(setLoginLoading(false));

    if (action.payload.flow === 'registration') {
      throw Error();
    }
  }
}

function* setAuthDataSaga(action: ISetAuthDataSaga) {
  yield* call(storageSetItem, STORAGE_KEYS.REFRESH_TOKEN, action.payload.refreshToken);

  yield* put(setAccessToken(action.payload.accessToken));
  yield* put(setRefreshToken(action.payload.refreshToken));

  // For initial auth flow, always set isInitialAuthDone
  if (action.payload.flow === 'initial-auth') {
    yield* put(fetchUserInfo());
    yield* put(fetchUserUsage());
    yield* put(setInitialAuthDone(true));
  } else if (action.payload.flow !== 'registration') {
    // For regular login flow
    yield* put(fetchUserInfo());
    yield* put(fetchUserUsage());
    yield* put(setInitialAuthDone(true));
  }
}

function* initialAuthSaga() {
  // Check for development authentication bypass
  if (isDevWithAuthBypass && devRefreshToken && devUserId) {
    // Store the dev refresh token in localStorage for consistency
    yield* call(storageSetItem, STORAGE_KEYS.REFRESH_TOKEN, devRefreshToken);

    // Set auth data with dev refresh token
    yield* put(
      setAuthData({
        accessToken: 'dev-access-token', // Temporary token, will be refreshed on first API call
        refreshToken: devRefreshToken,
        flow: 'dev-bypass',
      })
    );

    // Set the user ID directly
    yield* put(setUserId(devUserId));

    // Mark initial auth as done
    yield* put(setInitialAuthDone(true));

    return;
  }

  // Check Firebase first - Firebase auth state persists automatically
  yield* call([authClient, authClient.authStateReady]);
  const firebaseUser = yield* call([authClient, authClient.getCurrentUser]);

  if (firebaseUser) {
    yield* put(setFirebaseAuthenticated(true));
    yield* put(fetchUserInfo());
    yield* put(fetchUserUsage());
    yield* put(setInitialAuthDone(true));
    return;
  }

  // Fall back to legacy refresh token flow for existing users
  const refreshToken = yield* call(storageGetItem, STORAGE_KEYS.REFRESH_TOKEN);

  if (!refreshToken) {
    yield* put(setInitialAuthDone(true));

    return;
  }

  try {
    const response = yield* call(callApi<{ refresh_token: string }, IAuthResponse>, {
      method: METHODS.POST,
      mainPath: PATHS.REFRESH_TOKEN,
      data: {
        refresh_token: refreshToken,
      },
    });

    yield* put(
      setAuthData({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
        flow: 'initial-auth',
      })
    );
  } catch {
    yield* call(storageRemoveItem, STORAGE_KEYS.REFRESH_TOKEN);
    yield* put(setInitialAuthDone(true));
  }
}

function* logoutSaga() {
  try {
    const currentUser = yield* call([authClient, authClient.getCurrentUser]);
    if (currentUser) {
      yield* call([authClient, authClient.signOut]);
    }
  } catch (error) {
    captureException(error);
  }

  // Remove legacy refresh token
  yield* call(storageRemoveItem, STORAGE_KEYS.REFRESH_TOKEN);

  // Reset Redux state
  yield* put(resetState());
}

export function* watchAuth() {
  yield* all([
    takeEvery(SIGN_IN, signInSaga),
    takeEvery(SET_AUTH_DATA, setAuthDataSaga),
    takeEvery(INITIAL_AUTH, initialAuthSaga),
    takeEvery(FETCH_USER_INFO, fetchUserInfoSaga),
    takeEvery(FETCH_USER_USAGE, fetchUserUsageSaga),
    takeEvery(LOGOUT, logoutSaga),
  ]);
}
